import React, { useState, useEffect } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import { Translate, translate, ValidatedField } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { DocUploadDialog } from 'app/entities/quotation/bought/components/doc-upload-dialog';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getQuotationDocuments, getFileContent, deleteAllFilesForQuotation } from 'app/entities/file/file.reducer';
import { toast } from 'react-toastify';
import { AUTHORITIES } from 'app/config/constants';

/**
 * DocUploadForm - Displays transaction reference number and uploaded document details
 *
 * @param {Object} props - Component props
 * @param {string} props.transactionRefNo - Transaction reference number
 * @param {boolean} props.responsiveFont - Whether to use responsive font sizing
 * @param {Object} props.quotationEntity - The quotation entity object
 * @param {Function} props.deleteDocCallback - Callback function when documents are deleted
 */
export const DocUploadForm = ({ transactionRefNo = '', responsiveFont = null, quotationEntity, updateDocCallback, account }) => {
  const dispatch = useAppDispatch();
  const store = useAppSelector(state => state); // Get the store to pass to Provider
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);

  // Get files from Redux store
  const files = useAppSelector(state => state.file.entities);
  const loading = useAppSelector(state => state.file.loading);
  const [downloading, setDownloading] = useState(null);
  const quotationId = quotationEntity?.id;

  // Fetch files when component mounts or quotationId changes
  useEffect(() => {
    if (quotationId) {
      dispatch(getQuotationDocuments(quotationId));
    }
  }, [quotationId]);

  const toggleDocDialog = () => {
    setIsDocDialogOpen(!isDocDialogOpen);
  };

  const handleDocumentUploadSuccess = () => {
    // Reload documents after successful upload
    if (quotationId) {
      updateDocCallback();
    }
  };

  // Convert file metadata to FileEntry objects for editing
  const mapFilesToFileEntries = () => {
    if (!files || files.length === 0) return [];

    return files.map(file => ({
      id: file.id,
      fileType: file.fileType,
      fileName: file.fileName,
      order: file.orderIndex || 0,
      existingFile: true, // Flag to indicate this is an existing file
    }));
  };

  const handleDeleteAllFiles = async () => {
    if (!quotationId) return;

    if (window.confirm(translate('comexApp.quotation.confirmDeleteAll'))) {
      try {
        // Delete all files
        await dispatch(deleteAllFilesForQuotation(quotationId)).unwrap();

        // Refresh documents
        dispatch(getQuotationDocuments(quotationId));
        updateDocCallback();

        toast.success(translate('comexApp.quotation.deleteAllSuccess'));
      } catch (error) {
        console.error('Error deleting files:', error);
        toast.error(translate('comexApp.quotation.deleteAllFailed'));
      }
    }
  };

  const handleDownloadFile = async fileId => {
    if (!fileId) return;

    setDownloading(fileId);

    try {
      const result: any = await dispatch(getFileContent(fileId)).unwrap();

      // Create blob from response (handle potential type issues)
      const blob = new Blob([result.data], { type: result.headers?.['content-type'] || 'application/pdf' });

      // Get filename from Content-Disposition header or use a default
      let filename = 'document.pdf';
      const contentDisposition = result.headers?.['content-disposition'];

      if (contentDisposition) {
        const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error(translate('comexApp.quotation.downloadFailed'));
    } finally {
      setDownloading(null);
    }
  };

  // Font size styles based on responsiveFont prop
  const fontStyle = {
    fontSize: responsiveFont ? '0.9rem' : '1rem',
    fontWeight: 500,
  };

  const labelStyle = {
    ...fontStyle,
    color: '#555',
    marginBottom: '4px',
  };

  const valueStyle = {
    ...fontStyle,
    color: '#000',
    marginBottom: '16px',
  };

  // Sort files by order if available
  const sortedFiles = [...(files || [])].sort((a, b) =>
    a.orderIndex !== undefined && b.orderIndex !== undefined ? a.orderIndex - b.orderIndex : 0
  );

  const handlePrint = () => {
    const printUrl = `/quotation/print-concat/${quotationId}`;
    const link = document.createElement('a');
    link.href = printUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const isBankStaffWithoutDocumentAccess = (): boolean => {
    return (
      account?.authorities?.includes(AUTHORITIES.BANK_STAFF) && !account?.authorities?.includes(AUTHORITIES.BANK_STAFF_DOCUMENT_ACCESS)
    );
  };

  return (
    <Row className="justify-content-center" style={{ marginTop: '12px' }}>
      <DocUploadDialog
        isOpen={isDocDialogOpen}
        toggle={toggleDocDialog}
        quotationId={quotationId}
        initialTransactionRefNo={transactionRefNo}
        initialFiles={mapFilesToFileEntries()}
        onSuccess={handleDocumentUploadSuccess}
        quotationEntity={quotationEntity}
      />

      <Col md="8" lg="6" className="round-border mt-1" style={{ padding: '28px 42px 28px 42px' }}>
        <Row className="mb-3">
          <Col xs={8}>
            <Typography variant="h6" component="div">
              <strong>{translate('comexApp.quotation.documents')}</strong>
            </Typography>
          </Col>
          <Col xs={12} className="d-flex justify-content-between">
            {(account?.authorities?.includes(AUTHORITIES.ALSHMOKH_ADMIN) ||
              account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) ||
              account?.authorities?.includes(AUTHORITIES.BANK_STAFF)) && (
              <>
                <Button
                  color="link"
                  className="px-1 mt-2"
                  style={{ fontSize: '11px', padding: '0 4px' }}
                  onClick={toggleDocDialog}
                  disabled={isBankStaffWithoutDocumentAccess()}
                >
                  {sortedFiles.length > 0 ? (
                    <Translate contentKey="comexApp.quotation.editDocs">Edit Documents</Translate>
                  ) : (
                    <Translate contentKey="comexApp.quotation.addDoc">Add Document</Translate>
                  )}
                </Button>
                {sortedFiles.length > 0 && (
                  <Button
                    color="link"
                    className="px-1 mt-2"
                    style={{ fontSize: '11px', padding: '0 4px' }}
                    onClick={handleDeleteAllFiles}
                    disabled={
                      isBankStaffWithoutDocumentAccess() ||
                      (quotationEntity?.bank?.requireDocumentUpload &&
                        quotationEntity?.status !== 'Bought' &&
                        quotationEntity?.localCommodityBidCertId !== null)
                    }
                  >
                    <Translate contentKey="comexApp.quotation.deleteAllDocuments">Delete All Documents</Translate>
                  </Button>
                )}
              </>
            )}
          </Col>
        </Row>

        {/* Transaction Reference Number */}
        {/* {*/}
        {/*  transactionRefNo &&*/}
        {/*  <Grid container spacing={2} className="mb-3">*/}
        {/*    <Grid item xs={4}>*/}
        {/*      <Typography style={labelStyle}>*/}
        {/*        {translate('comexApp.quotation.transactionRefNo')}:*/}
        {/*      </Typography>*/}
        {/*    </Grid>*/}
        {/*    <Grid item xs={8}>*/}
        {/*      <ValidatedField*/}
        {/*        id="tx_ref_no"*/}
        {/*        name="tx_ref_no"*/}
        {/*        data-cy="tx_ref_no"*/}
        {/*        type="text"*/}
        {/*        disabled*/}
        {/*        value={transactionRefNo || translate('comexApp.quotation.notAvailable')}*/}
        {/*      />*/}
        {/*    </Grid>*/}
        {/*  </Grid>*/}
        {/* }*/}

        {/* Documents List */}
        {loading ? (
          <div className="text-center mt-3">
            <FontAwesomeIcon icon={faSpinner} spin /> <Translate contentKey="comexApp.quotation.loading">Loading documents...</Translate>
          </div>
        ) : sortedFiles.length > 0 ? (
          <TableContainer component={Paper} elevation={0} className="mt-3" sx={{ border: '1px solid #eee' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width="8%">{translate('comexApp.quotation.index')}</TableCell>
                  <TableCell width="30%">{translate('comexApp.quotation.fileType')}</TableCell>
                  <TableCell width="50%">{translate('comexApp.quotation.fileName')}</TableCell>
                  <TableCell width="12%" align="center">
                    {translate('comexApp.quotation.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFiles.map((file, index) => (
                  <TableRow key={file.id || index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{file.fileType}</TableCell>
                    <TableCell>{file.fileName}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadFile(file.id)}
                        disabled={downloading === file.id || isBankStaffWithoutDocumentAccess()}
                        color="primary"
                      >
                        {downloading === file.id ? (
                          <FontAwesomeIcon icon={faSpinner} spin size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faDownload} size="sm" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography style={{ color: '#666' }} className="mt-2">
            {translate('comexApp.quotation.noDocumentsUploaded')}
          </Typography>
        )}

        {transactionRefNo && (
          <Row className="justify-content-end">
            <Button
              style={{ width: 100 }}
              color="link"
              className="px-1"
              id="save-entity"
              data-cy="entityCreateSaveButton"
              onClick={handlePrint}
              disabled={isBankStaffWithoutDocumentAccess()}
            >
              <Translate contentKey="comexApp.quotation.printBtnLabel">Print</Translate>
            </Button>
          </Row>
        )}
      </Col>
    </Row>
  );
};
